import { ChangeEvent, useEffect, useRef, useState } from "react";
import { QueryModalFilterOption } from "types";
import classNames from "classnames";

import OptionsDropdown from "../OptionsDropdown";

import SearchIcon from "assets/icons/SearchIcon";
import XIcon from "assets/icons/XIcon";

import styles from "./styles.module.scss";
import InputTabs from "./InputTabs";

export interface QueryModalInputProps {
  searchText: string;
  selected?: number[];
  numberOnly?: boolean;
  placeholder?: string;
  showSearchIcon?: boolean;
  options?: QueryModalFilterOption[];
  mode?: "text" | "multiple" | "single";
  onSelect?: (id: number) => void;
  onRemove?: (id: number) => void;
  onInputChange: (value: string) => void;
}

export default function QueryModalInput({
  searchText,
  options = [],
  selected = [],
  mode = "text",
  numberOnly = false,
  placeholder = "Search...",
  onInputChange,
  onSelect = () => {},
  onRemove = () => {},
}: QueryModalInputProps) {
  const [showOptions, setShowOptions] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter((opt) =>
    opt.text.toLowerCase().includes(searchText.toLowerCase()),
  );
  const tabs = options.filter((option) => selected.includes(option.id));
  const multiSelect = mode === "multiple";
  const showDropdown = showOptions && mode !== "text";
  const showSearchIcon = !(mode === "text" && !searchText);

  const containerClassnames = classNames(styles.container, "inputContainer", {
    [styles.numberContainer]: numberOnly,
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onInputChange(e.target.value);
  };

  const handleClearClick = () => {
    onInputChange("");
    setShowOptions(false);
  };

  const handleOptionSelection = (id: number) => {
    onSelect(id);
    if (!multiSelect) {
      setShowOptions(false);
    }
  };

  const renderDisplay = () => {
    if (!selected.length) {
      return (
        <span
          data-testid="query-modal-input-placeholder"
          className={classNames(
            styles.placeholder,
            "query-modal-input-placeholder",
          )}
        >
          {placeholder}
        </span>
      );
    } else {
      return <InputTabs tabs={tabs} onRemove={onRemove} />;
    }
  };

  const handleOutsideClick = (e: any) => {
    if (!containerRef.current?.contains(e.target)) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    if (showOptions) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };

    //eslint-disable-next-line
  }, [showOptions]);

  return (
    <div
      ref={containerRef}
      className={containerClassnames}
      data-testid="query-modal-input"
    >
      {showSearchIcon && (
        <SearchIcon dataTestId="query-modal-input-search-icon" />
      )}

      <div className={styles.labelsContainer}>
        {showOptions || mode === "text" ? (
          <>
            <input
              autoFocus
              ref={inputRef}
              value={searchText}
              className={classNames(styles.input, "input")}
              placeholder={placeholder}
              data-testid="query-modal-input-field"
              type={numberOnly ? "number" : undefined}
              onChange={handleInputChange}
            />
            {searchText && (
              <div
                className={styles.xIcon}
                onClick={handleClearClick}
                data-testid="query-modal-input-clear-button"
              >
                <XIcon variant="new" />
              </div>
            )}
          </>
        ) : (
          <div
            className={styles.displayContainer}
            onClick={(e) => {
              e.stopPropagation();
              setShowOptions(true);
            }}
            data-testid="query-modal-input-display-container"
          >
            {renderDisplay()}
          </div>
        )}
      </div>

      {showDropdown && (
        <OptionsDropdown
          selected={selected}
          multiSelect={multiSelect}
          options={filteredOptions}
          onSelect={handleOptionSelection}
        />
      )}
    </div>
  );
}
